import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SearchFilter from '../components/SearchFilter';
import BookmarkButton from '../components/BookmarkButton';
import { useLanguage } from '../context/LanguageContext';
import { reviewsApi, moviesApi, languageToTMDB } from '../services/api';
import { Calendar, Clock, ArrowRight, Film } from 'lucide-react';

const ReviewsPage = () => {
  const { t, language } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [tmdbResults, setTmdbResults] = useState([]);
  const [movieDetails, setMovieDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    genre: 'all',
    category: 'all',
    sort: 'newest'
  });

  // Fetch reviews from backend with search/filter params
  const fetchReviews = useCallback(async (search = '', currentFilters = {}) => {
    try {
      setLoading(true);
      setTmdbResults([]); // Reset TMDB results
      const tmdbLang = languageToTMDB[language] || 'en-US';

      // Map frontend sort to backend sort_by
      const sortMap = {
        popularity: 'newest',
        newest: 'newest',
        oldest: 'oldest',
        title: 'title_asc',
        matrix_score: 'rating',
        release_date: 'newest'
      };

      // Fetch reviews with filters from backend
      const reviewsData = await reviewsApi.getAll({
        search: search || undefined,
        category: currentFilters.category !== 'all' ? currentFilters.category : undefined,
        genre: currentFilters.genre !== 'all' ? currentFilters.genre : undefined,
        sort_by: sortMap[currentFilters.sort] || 'newest',
        limit: 50
      });
      
      setReviews(reviewsData);

      // If searching and no reviews found, search TMDB
      if (search && search.length >= 2 && reviewsData.length === 0) {
        try {
          const tmdbData = await moviesApi.searchAll(search, 1, tmdbLang);
          // Filter to only show movies with posters
          const moviesWithPosters = (tmdbData.results || []).filter(m => m.poster);
          setTmdbResults(moviesWithPosters);
        } catch (err) {
          console.error('Error searching TMDB:', err);
        }
      } else if (search && search.length >= 2) {
        // Also search TMDB for additional results
        try {
          const tmdbData = await moviesApi.searchAll(search, 1, tmdbLang);
          // Filter out movies that already have reviews and movies without posters
          const reviewedIds = reviewsData.map(r => r.tmdb_id);
          const additionalMovies = (tmdbData.results || [])
            .filter(m => !reviewedIds.includes(m.id) && m.poster);
          setTmdbResults(additionalMovies.slice(0, 10)); // Limit additional results
        } catch (err) {
          console.error('Error searching TMDB:', err);
        }
      }

      // Fetch movie details for display
      const details = { ...movieDetails };
      await Promise.all(
        reviewsData.map(async (review) => {
          if (!details[review.tmdb_id]) {
            try {
              const movieDetail = await moviesApi.getDetail(review.tmdb_id, tmdbLang);
              details[review.tmdb_id] = movieDetail;
            } catch (err) {
              console.error(`Error fetching movie ${review.tmdb_id}:`, err);
            }
          }
        })
      );
      setMovieDetails(details);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  }, [language, movieDetails]);

  // Initial load
  useEffect(() => {
    fetchReviews(searchQuery, filters);
  }, [language]); // Only re-fetch on language change

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
    fetchReviews(query, filters);
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchReviews(searchQuery, newFilters);
  };

  if (loading && reviews.length === 0) {
    return (
      <div className="bg-[#0B0F0E] min-h-screen">
        <Header />
        <main className="pt-32 pb-20 flex items-center justify-center">
          <div className="w-12 h-12 border-2 border-[#52ea88] border-t-transparent rounded-full animate-spin"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#0B0F0E] min-h-screen">
      <Header />

      <main className="pt-32 pb-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
          {/* Page Header */}
          <div className="space-y-4 mb-8">
            <h1 className="text-4xl md:text-5xl font-semibold text-[#E6FFF6]">
              {t('nav.reviews')}
            </h1>
            <p className="text-[#9FBFB4] text-lg max-w-2xl">
              {t('reviews.description')}
            </p>
            <div className="w-20 h-1 bg-[#52ea88]"></div>
          </div>

          {/* Search & Filter */}
          <SearchFilter 
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            showGenres={true}
            showYears={false}
            initialFilters={filters}
          />

          {/* Results count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-[#9FBFB4] text-sm">
              {loading ? t('reviews.searching') : `${t('reviews.found')} ${reviews.length} ${t('reviews.reviewsCount')}`}
              {searchQuery && ` ${t('reviews.forQuery')} "${searchQuery}"`}
            </p>
            {loading && reviews.length > 0 && (
              <div className="w-5 h-5 border-2 border-[#52ea88] border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>

          {/* Reviews Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {reviews.map((review) => {
              const movie = movieDetails[review.tmdb_id] || {};
              const genres = movie.genres || review.genres || [];
              const matrixScore = review.signals ? Math.round(
                Object.values(review.signals).reduce((sum, v) => sum + v, 0) / 5
              ) : 0;

              return (
                <div
                  key={review.id}
                  className="group relative flex flex-col sm:flex-row gap-6 p-6 bg-[#121917] border border-[#52ea88]/10 hover:border-[#52ea88]/40 transition-all duration-300"
                >
                  {/* Bookmark Button */}
                  <div className="absolute top-4 right-4 z-10">
                    <BookmarkButton reviewId={review.id} />
                  </div>

                  {/* Poster */}
                  <Link to={`/review/${review.id}`} className="w-full sm:w-40 flex-shrink-0">
                    {(movie.poster || review.poster_path) ? (
                      <img
                        src={movie.poster || `https://image.tmdb.org/t/p/w300${review.poster_path}`}
                        alt={movie.title || review.title}
                        className="w-full h-auto sm:h-60 object-cover"
                      />
                    ) : (
                      <div className="w-full h-60 bg-[#0B0F0E] flex items-center justify-center">
                        <span className="text-[#9FBFB4]/50">{t('reviews.noImage')}</span>
                      </div>
                    )}
                  </Link>

                  {/* Content */}
                  <div className="flex-1 space-y-4">
                    {/* Genre Tags & Matrix Score */}
                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-2">
                        {genres.slice(0, 2).map((g, idx) => (
                          <span
                            key={g.id || idx}
                            className="px-2 py-1 text-[#52ea88] text-xs border border-[#52ea88]/30"
                          >
                            {g.name}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 bg-[#52ea88]/10 border border-[#52ea88]/30">
                        <span className="text-[#52ea88] text-sm font-bold">{matrixScore}</span>
                        <span className="text-[#9FBFB4] text-xs">Matrix</span>
                      </div>
                    </div>

                    {/* Title */}
                    <Link to={`/review/${review.id}`}>
                      <h2 className="text-2xl font-semibold text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors">
                        {review.title}
                      </h2>
                    </Link>

                    {/* Meta */}
                    <div className="flex items-center gap-4 text-[#9FBFB4] text-sm">
                      {(movie.year || review.release_date) && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {movie.year || review.release_date?.split('-')[0]}
                        </span>
                      )}
                      {(movie.runtime || review.runtime) && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {movie.runtime || review.runtime} min
                        </span>
                      )}
                    </div>

                    {/* Excerpt */}
                    <p className="text-[#9FBFB4]/80 text-sm line-clamp-3 leading-relaxed">
                      {review.excerpt || review.overview}
                    </p>

                    {/* Director & CTA */}
                    <div className="flex items-center justify-between pt-2">
                      {(movie.director || review.director) && (
                        <span className="text-[#9FBFB4] text-sm">
                          <span className="text-[#52ea88]">{t('reviews.director')}</span> {movie.director || review.director}
                        </span>
                      )}
                      <Link 
                        to={`/review/${review.id}`}
                        className="flex items-center gap-2 text-[#52ea88] text-sm group-hover:gap-3 transition-all"
                      >
                        {t('reviews.readReview')}
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* TMDB Search Results (movies without reviews) */}
          {tmdbResults.length > 0 && (
            <div className="mt-16">
              <h2 className="text-2xl font-semibold text-[#E6FFF6] mb-6 flex items-center gap-3">
                <Film className="w-6 h-6 text-[#52ea88]" />
                {t('reviews.otherMovies') || 'Other Movies on TMDB'}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {tmdbResults.map((movie) => (
                  <Link
                    key={movie.id}
                    to={`/movie/${movie.id}`}
                    className="group block"
                  >
                    <div className="relative aspect-[2/3] overflow-hidden bg-[#1a2420] rounded-lg">
                      {movie.poster ? (
                        <img
                          src={movie.poster}
                          alt={movie.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="w-12 h-12 text-[#52ea88]/30" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F0E] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform">
                        <span className="text-xs text-[#52ea88] bg-[#0B0F0E]/80 px-2 py-1 rounded">
                          {t('reviews.noReviewYet') || 'No review yet'}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <h3 className="text-sm font-medium text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors line-clamp-1">
                        {movie.title}
                      </h3>
                      <p className="text-xs text-[#9FBFB4]">{movie.year}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {reviews.length === 0 && tmdbResults.length === 0 && !loading && (
            <div className="text-center py-20">
              <p className="text-[#9FBFB4]/70 text-lg">{t('reviews.noResults')}</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilters({ genre: 'all', category: 'all', sort: 'newest' });
                  fetchReviews('', { genre: 'all', category: 'all', sort: 'newest' });
                }}
                className="text-[#52ea88] hover:underline mt-2"
              >
                {t('reviews.clearFilters')}
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReviewsPage;
